import {
  RefundOrderByWithRelationInput,
  RefundsQuery,
  RefundWhereInput,
  QueryMode,
  SortOrder,
  RefundStatus,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Badge,
  Group,
  Paper,
  Stack,
  TextInput,
  Select,
  NumberInput,
  Text,
  Tooltip,
  Avatar,
  Indicator,
  Anchor,
  Button,
  Alert,
  Checkbox,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconEdit, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { RefundUpsertModal } from '../components/RefundUpsertModal';
import { OrderLineType, OrderLineStatus } from '@/__generated__/graphql';
import { deleteRefundDocument } from '../graphql/delete-refund';
import {
  countRefundsDocument,
  refundsDocument,
  refundStatisticsDocument,
} from '../graphql/queries';
import { Link } from 'react-router-dom';
import { updateRefundDocument } from '../graphql/upsert-refund';
import { DatePickerInput } from '@mantine/dates';

const refundStatusTranslations = {
  [RefundStatus.Created]: 'Vytvorená',
  [RefundStatus.Exported]: 'Exportovaná',
  [RefundStatus.Processed]: 'Spracovaná',
};

const refundStatusColors = {
  [RefundStatus.Created]: 'blue',
  [RefundStatus.Exported]: 'orange',
  [RefundStatus.Processed]: 'green',
};

const lineStatusColors = {
  [OrderLineStatus.Created]: 'gray',
  [OrderLineStatus.Packed]: 'blue',
  [OrderLineStatus.Confirmed]: 'green',
  [OrderLineStatus.Reviewing]: 'orange',
  [OrderLineStatus.Returned]: 'red',
};

const lineStatusTranslations = {
  [OrderLineStatus.Created]: 'Objednané',
  [OrderLineStatus.Packed]: 'Zabalené',
  [OrderLineStatus.Confirmed]: 'Potvrdené',
  [OrderLineStatus.Reviewing]: 'V riešení',
  [OrderLineStatus.Returned]: 'Vrátené',
};

const getDayDiff = (date: Date | string) =>
  dayjs().startOf('day').diff(dayjs(date).startOf('day'), 'day');

export const RefundList = () => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const [selectedRefunds, setSelectedRefunds] = useState<RefundsQuery['refunds']>([]);
  const [filterStatus, setFilterStatus] = useState<RefundWhereInput>({
    status: { equals: RefundStatus.Created },
  });
  const [where] = useDebouncedValue(filterStatus, 50);

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<RefundsQuery['refunds'][number]>
  >({
    columnAccessor: 'createdAt',
    direction: 'desc',
  });
  const orderBy = useMemo<RefundOrderByWithRelationInput>(() => {
    switch (sortStatus.columnAccessor) {
      case 'customer.name':
        return { customer: { name: sortStatus.direction as SortOrder } };
      case 'iban':
        return { iban: { sort: sortStatus.direction as SortOrder } };
      default:
        return { [sortStatus.columnAccessor]: sortStatus.direction };
    }
  }, [sortStatus]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: count } = useQuery(countRefundsDocument, { variables: { where } });

  const { data, loading, refetch } = useQuery(refundsDocument, {
    variables: {
      skip: pageSize * page - pageSize,
      take: pageSize,
      orderBy,
      where,
    },
  });

  const [updateRefund, updateRefundMutation] = useMutation(updateRefundDocument, {
    refetchQueries: [refundsDocument],
    awaitRefetchQueries: true,
  });

  const [deleteRefund, deleteRefundMutation] = useMutation(deleteRefundDocument, {
    refetchQueries: [refundsDocument],
    awaitRefetchQueries: true,
  });

  const createdAt =
    dateRange[0] || dateRange[1]
      ? {
          ...(dateRange[0] && { gte: dateRange[0] }),
          ...(dateRange[1] && { lte: dateRange[1] }),
        }
      : undefined;

  const refundStatistics = useQuery(refundStatisticsDocument, {
    variables: {
      where: {
        ...(createdAt && { createdAt }),
      },
    },
  });

  const createdRefundsWithIban = refundStatistics.data?.refunds.filter(
    (refund) => refund.status === RefundStatus.Created && refund.iban
  );
  const createdRefundsWithIbanAmount =
    createdRefundsWithIban?.reduce((acc, refund) => acc + refund.amount, 0) ?? 0;

  const createdRefundsWithoutIban = refundStatistics.data?.refunds.filter(
    (refund) => refund.status === RefundStatus.Created && !refund.iban
  );
  const createdRefundsWithoutIbanAmount =
    createdRefundsWithoutIban?.reduce((acc, refund) => acc + refund.amount, 0) ?? 0;

  const exportedRefunds = refundStatistics.data?.refunds.filter(
    (refund) => refund.status === RefundStatus.Exported
  );
  const exportedRefundsAmount =
    exportedRefunds?.reduce((acc, refund) => acc + refund.amount, 0) ?? 0;

  const processedRefunds = refundStatistics.data?.refunds.filter(
    (refund) => refund.status === RefundStatus.Processed
  );
  const processedRefundsAmount =
    processedRefunds?.reduce((acc, refund) => acc + refund.amount, 0) ?? 0;

  const refundsToExport = refundStatistics.data?.refunds.filter(
    (refund) =>
      refund.status === RefundStatus.Created &&
      refund.iban &&
      (getDayDiff(refund.createdAt) >= 14 || refund.reason === 'chyba z nasej strany')
  );

  const refundsToExportAmount =
    refundsToExport?.reduce((acc, refund) => acc + refund.amount, 0) ?? 0;

  return (
    <Stack>
      <Paper p="md" withBorder component={Group}>
        <Group>
          <TextInput
            placeholder="Dôvod..."
            value={filterStatus.reason?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                reason: { contains: event.currentTarget.value, mode: QueryMode.Insensitive },
              }));
            }}
          />
          <TextInput
            placeholder="IBAN..."
            value={filterStatus.iban?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                iban: { contains: event.currentTarget.value, mode: QueryMode.Insensitive },
              }));
            }}
          />
          <Checkbox
            label="IBAN"
            checked={filterStatus.iban?.not === null}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                iban: event.currentTarget.checked ? { not: null } : undefined,
              }));
            }}
          />
          <Checkbox
            label="Kredity"
            checked={filterStatus.iban?.equals === null}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                iban: event.currentTarget.checked ? { equals: null } : undefined,
              }));
            }}
          />
          <NumberInput
            placeholder="Suma..."
            value={filterStatus.amount?.equals ?? undefined}
            onChange={(value) => {
              setFilterStatus((prev) => ({
                ...prev,
                amount: value !== '' ? { equals: +value } : undefined,
              }));
            }}
          />
          <Select
            clearable
            placeholder="Status..."
            data={Object.entries(refundStatusTranslations).map(([value, label]) => ({
              value,
              label,
            }))}
            value={filterStatus.status?.equals ?? ''}
            onChange={(value) => {
              setFilterStatus((prev) => ({
                ...prev,
                status: value ? { equals: value as RefundStatus } : undefined,
              }));
            }}
          />
          <TextInput
            placeholder="Zákazník..."
            value={filterStatus.customer?.is?.name?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                customer:
                  event.currentTarget.value.length > 0
                    ? {
                        is: {
                          name: {
                            contains: event.currentTarget.value,
                            mode: QueryMode.Insensitive,
                          },
                        },
                      }
                    : undefined,
              }));
            }}
          />
          <DatePickerInput
            label="Dátum"
            type="range"
            value={dateRange}
            onChange={setDateRange}
            allowSingleDateInRange
            clearable
            renderDay={(date) => {
              const day = dayjs(date);

              const hasRefund = refundStatistics.data?.refunds.some((refund) =>
                dayjs(refund.createdAt).isSame(day, 'day')
              );

              return hasRefund ? (
                <Indicator size={6} color="blue" offset={-5}>
                  {day.date()}
                </Indicator>
              ) : (
                <div>{day.date()}</div>
              );
            }}
          />
        </Group>
        <Group justify="space-between">
          <Text c={refundStatusColors[RefundStatus.Created]}>
            Kredity: {createdRefundsWithoutIban?.length ?? 0} ({createdRefundsWithoutIbanAmount} €)
          </Text>
          <Text c={refundStatusColors[RefundStatus.Created]}>
            Nevrátené: {createdRefundsWithIban?.length ?? 0} ({createdRefundsWithIbanAmount} €)
          </Text>
          <Text c={refundStatusColors[RefundStatus.Exported]}>
            Exportované: {exportedRefunds?.length ?? 0} ({exportedRefundsAmount} €)
          </Text>
          <Text c={refundStatusColors[RefundStatus.Processed]}>
            Spracované: {processedRefunds?.length ?? 0} ({processedRefundsAmount} €)
          </Text>
          <Button
            color={(refundsToExport?.length ?? 0) > 0 ? 'red' : 'gray'}
            disabled={(refundsToExport?.length ?? 0) === 0}
            onClick={() => {
              if (filterStatus.id) setFilterStatus({ status: { equals: RefundStatus.Created } });
              else
                setFilterStatus({
                  id: { in: refundsToExport?.map((refund) => refund.id) ?? [] },
                });
            }}
            rightSection={filterStatus.id ? <IconX size={16} /> : undefined}
          >
            Na vrátenie ({refundsToExport?.length ?? 0} - {refundsToExportAmount} €)
          </Button>
        </Group>
        {selectedRefunds.length > 0 && (
          <Group justify="flex-end">
            <Button
              onClick={() =>
                modals.openConfirmModal({
                  size: '75%',
                  title: `Export ${selectedRefunds.length} reklamácií?`,
                  labels: { confirm: 'Exportovať', cancel: 'Zrušiť' },
                  children: (
                    <Stack>
                      <Alert title="Export reklamácií" color="blue">
                        Tieto reklamácie budú exportované do formátu XML pre Prima Banka. Počet
                        reklamácií: {selectedRefunds.length}. Celková suma:{' '}
                        {selectedRefunds.reduce((acc, refund) => acc + refund.amount, 0)} €.
                      </Alert>
                      <DataTable
                        withTableBorder
                        striped
                        highlightOnHover
                        records={selectedRefunds}
                        rowColor={({ id, reason }) => {
                          if (reason === 'chyba z nasej strany') return 'red';
                          if (refundsToExport?.some((refund) => refund.id === id)) return 'orange';
                        }}
                        columns={[
                          {
                            accessor: 'customer.name',
                            title: 'Zákazník',
                            render: ({ customer }) => (
                              <Group wrap="nowrap">
                                <Avatar src={customer.avatar} />
                                <Text inherit>{customer.name}</Text>
                              </Group>
                            ),
                          },
                          {
                            accessor: 'reason',
                            title: 'Dôvod',
                            render: ({ reason, note }) =>
                              note ? (
                                <Tooltip multiline label={note} withArrow>
                                  <Indicator inline>
                                    <Text inherit>{reason}</Text>
                                  </Indicator>
                                </Tooltip>
                              ) : (
                                <Text inherit>{reason}</Text>
                              ),
                          },
                          {
                            accessor: 'amount',
                            title: 'Suma',
                            render: (refund) => `${refund.amount} €`,
                          },
                          {
                            accessor: 'status',
                            title: 'Stav',
                            render: ({ status }) => (
                              <Badge color={refundStatusColors[status]}>
                                {refundStatusTranslations[status]}
                              </Badge>
                            ),
                          },
                          {
                            accessor: 'createdAt',
                            title: 'Dátum',
                            render: ({ createdAt, updatedAt, exportedAt, processedAt }) => (
                              <Stack gap={0}>
                                <Text inherit>
                                  {dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}
                                </Text>
                                {updatedAt > createdAt && (
                                  <Text inherit c="dimmed" size="sm">
                                    {dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                                  </Text>
                                )}
                                {exportedAt && (
                                  <Text
                                    inherit
                                    c={refundStatusColors[RefundStatus.Exported]}
                                    size="sm"
                                  >
                                    {dayjs(exportedAt).format('DD.MM.YYYY HH:mm:ss')}
                                  </Text>
                                )}
                                {processedAt && (
                                  <Text
                                    inherit
                                    c={refundStatusColors[RefundStatus.Processed]}
                                    size="sm"
                                  >
                                    {dayjs(processedAt).format('DD.MM.YYYY HH:mm:ss')}
                                  </Text>
                                )}
                              </Stack>
                            ),
                          },
                        ]}
                      />
                    </Stack>
                  ),
                  onConfirm: () => {
                    window
                      .open(
                        `https://api.laskykrasky.sk/api/refund/export?refundIds=${selectedRefunds
                          .map((refund) => refund.id)
                          .join(',')}`,
                        '_blank'
                      )
                      ?.focus();

                    setTimeout(() => refetch, 1000);
                  },
                })
              }
            >
              Exportovať ({selectedRefunds.length} -{' '}
              {selectedRefunds.reduce((acc, refund) => acc + refund.amount, 0)} €)
            </Button>
            <Button
              color="teal"
              loading={updateRefundMutation.loading}
              onClick={() =>
                modals.openConfirmModal({
                  size: '75%',
                  title: `Spracovať ${selectedRefunds.length} reklamácií?`,
                  labels: { confirm: 'Spracovať', cancel: 'Zrušiť' },
                  children: (
                    <Stack>
                      <Alert title="Spracovanie reklamácií" color="blue">
                        Tieto reklamácie budú spracované. Počet reklamácií: {selectedRefunds.length}
                        . Celková suma:{' '}
                        {selectedRefunds.reduce((acc, refund) => acc + refund.amount, 0)} €.
                      </Alert>
                      <DataTable
                        withTableBorder
                        striped
                        highlightOnHover
                        records={selectedRefunds}
                        columns={[
                          {
                            accessor: 'customer.name',
                            title: 'Zákazník',
                            render: ({ customer }) => (
                              <Group wrap="nowrap">
                                <Avatar src={customer.avatar} />
                                <Text inherit>{customer.name}</Text>
                              </Group>
                            ),
                          },
                          {
                            accessor: 'reason',
                            title: 'Dôvod',
                            render: ({ reason, note }) =>
                              note ? (
                                <Tooltip multiline label={note} withArrow>
                                  <Indicator inline>
                                    <Text inherit>{reason}</Text>
                                  </Indicator>
                                </Tooltip>
                              ) : (
                                <Text inherit>{reason}</Text>
                              ),
                          },
                          {
                            accessor: 'amount',
                            title: 'Suma',
                            render: (refund) => `${refund.amount} €`,
                          },
                          {
                            accessor: 'status',
                            title: 'Stav',
                            render: ({ status }) => (
                              <Badge color={refundStatusColors[status]}>
                                {refundStatusTranslations[status]}
                              </Badge>
                            ),
                          },
                          {
                            accessor: 'createdAt',
                            title: 'Dátum',
                            render: ({ createdAt, updatedAt, exportedAt, processedAt }) => (
                              <Stack gap={0}>
                                <Text inherit>
                                  {dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}
                                </Text>
                                {updatedAt > createdAt && (
                                  <Text inherit c="dimmed" size="sm">
                                    {dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                                  </Text>
                                )}
                                {exportedAt && dayjs(exportedAt).format('DD.MM.YYYY HH:mm:ss')}
                                {processedAt && dayjs(processedAt).format('DD.MM.YYYY HH:mm:ss')}
                              </Stack>
                            ),
                          },
                        ]}
                      />
                    </Stack>
                  ),
                  onConfirm: async () => {
                    await Promise.all(
                      selectedRefunds.map((refund) =>
                        updateRefund({
                          variables: {
                            where: { id: refund.id },
                            data: {
                              status: { set: RefundStatus.Processed },
                              processedAt: { set: new Date() },
                            },
                          },
                        })
                      )
                    );

                    refetch();
                  },
                })
              }
            >
              Spracovať ({selectedRefunds.length} -{' '}
              {selectedRefunds.reduce((acc, refund) => acc + refund.amount, 0)} €)
            </Button>
          </Group>
        )}
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        pinLastColumn
        borderRadius="sm"
        minHeight={250}
        records={data?.refunds}
        selectedRecords={selectedRefunds}
        onSelectedRecordsChange={setSelectedRefunds}
        fetching={loading}
        totalRecords={count?.countRefunds}
        recordsPerPage={pageSize}
        recordsPerPageOptions={[5, 10, 25, 50, 100]}
        page={page}
        onPageChange={setPage}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          {
            accessor: 'customer.name',
            title: 'Zákazník',
            render: ({ customer }) => (
              <Group wrap="nowrap">
                <Avatar src={customer.avatar} />
                <Text inherit>{customer.name}</Text>
              </Group>
            ),
            sortable: true,
          },
          {
            accessor: 'reason',
            title: 'Dôvod',
            render: ({ reason, note }) =>
              note ? (
                <Tooltip multiline label={note} withArrow>
                  <Indicator inline>
                    <Text inherit>{reason}</Text>
                  </Indicator>
                </Tooltip>
              ) : (
                <Text inherit>{reason}</Text>
              ),
            sortable: true,
          },
          {
            accessor: 'amount',
            title: 'Suma',
            sortable: true,
            render: ({ amount }) => `${amount} €`,
          },
          {
            accessor: 'iban',
            title: 'IBAN',
            sortable: true,
          },
          {
            accessor: 'status',
            title: 'Stav',
            sortable: true,
            render: ({ status }) => (
              <Badge color={refundStatusColors[status]}>{refundStatusTranslations[status]}</Badge>
            ),
          },
          {
            accessor: 'createdAt',
            title: 'Dátum',
            render: ({ createdAt, updatedAt, exportedAt, processedAt }) => (
              <Stack gap={0}>
                <Text inherit>{dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                {updatedAt > createdAt && (
                  <Text inherit c="dimmed" size="sm">
                    {dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                )}
                {exportedAt && dayjs(exportedAt).format('DD.MM.YYYY HH:mm:ss')}
                {processedAt && dayjs(processedAt).format('DD.MM.YYYY HH:mm:ss')}
              </Stack>
            ),
            sortable: true,
          },
          {
            accessor: 'daysToReturn',
            title: 'Na vrátenie',
            render: ({ createdAt }) => {
              const daysSinceCreated = getDayDiff(createdAt);
              return daysSinceCreated >= 14 ? (
                <Badge color="red" variant="filled">
                  {daysSinceCreated} dní
                </Badge>
              ) : daysSinceCreated >= 10 ? (
                <Badge color="orange" variant="light">
                  {daysSinceCreated} dní
                </Badge>
              ) : (
                <Badge color="gray" variant="light">
                  {daysSinceCreated} dní
                </Badge>
              );
            },
            sortable: false,
          },
          {
            accessor: 'actions',
            title: (
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() =>
                  modals.open({
                    title: 'Nová reklamácia',
                    children: <RefundUpsertModal />,
                    size: '80%',
                  })
                }
              >
                <IconPlus size={16} />
              </ActionIcon>
            ),
            textAlign: 'right',
            render: (refund) => (
              <Group gap={4} justify="right" wrap="nowrap">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="blue"
                  onClick={(e) => {
                    e.stopPropagation();

                    modals.open({
                      title: 'Upraviť reklamáciu',
                      children: <RefundUpsertModal refund={refund} />,
                      centered: true,
                      size: '80%',
                    });
                  }}
                >
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  loading={deleteRefundMutation.loading}
                  onClick={(e) => {
                    e.stopPropagation();

                    modals.openConfirmModal({
                      title: `Zmazať reklamáciu ${refund.id}?`,
                      centered: true,
                      labels: { confirm: 'Zmazať', cancel: 'Zrušiť' },
                      confirmProps: { color: 'red' },
                      onConfirm: () =>
                        void deleteRefund({ variables: { where: { id: refund.id } } }),
                    });
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        rowExpansion={{
          content: ({ record }) => (
            <DataTable
              withTableBorder
              striped
              highlightOnHover
              records={record.lines ?? []}
              columns={[
                {
                  accessor: 'order',
                  title: 'Objednávka',
                  render: ({ order }) => (
                    <Anchor inherit to={`/order/${order.id}`} component={Link}>
                      Objednávka č. {order.package}
                    </Anchor>
                  ),
                },
                {
                  title: 'Kód',
                  accessor: 'code',
                  render: (line) => (line.type === OrderLineType.Product ? line.code : '-'),
                },
                {
                  title: 'Názov',
                  accessor: 'name',
                  render: ({ description, name }) =>
                    description ? (
                      <Tooltip multiline label={description} withArrow>
                        <Indicator inline>
                          <Text inherit>{name}</Text>
                        </Indicator>
                      </Tooltip>
                    ) : (
                      <Text inherit>{name}</Text>
                    ),
                },
                { accessor: 'price', title: 'Suma', render: (line) => `${line.price} €` },
                {
                  accessor: 'status',
                  title: 'Stav',
                  render: ({ status }) => (
                    <Badge color={lineStatusColors[status]}>{lineStatusTranslations[status]}</Badge>
                  ),
                },
              ]}
            />
          ),
        }}
      />
    </Stack>
  );
};
